import React, { useEffect, useRef } from 'react';

// ── A DOCUMENT, IN FRONT OF EVERYTHING ───────────────────────────────────
//
// Julian wanted to click an invoice number on a statement and look at the
// bill without losing the statement underneath it. Not a new tab, not a
// screen he has to find his way back from: a window in front, an X in the
// top right, and clicking outside it closes it.
//
// It wraps PrintDoc rather than drawing a second invoice layout. There is
// one renderer for the document that is also the printed page, and a copy
// of it here would drift from the real one the week after it was written.
//
// Printing still works. The print rules hide everything and bring back
// `.print-doc`, which is inside this frame, and `visibility:hidden` takes
// the dark scrim with it.
export default function DocPeek({ onClose, label = 'Document', children }) {
  const box = useRef(null);

  // Escape closes, same as the X and the scrim. A window with one way out is
  // a window people close with the browser's Back button, which leaves the
  // page they were reading.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The page behind must not scroll while this is open: on a phone a scrim
  // that scrolls the page underneath feels broken, and the document has its
  // own scroll.
  useEffect(() => {
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = was; };
  }, []);

  return (
    <div
      className="peek"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      // Mousedown rather than click, and only when the press STARTED on the
      // scrim. A drag that begins on the document and ends outside it is
      // somebody selecting text, not somebody asking to close.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="peek-box" ref={box}>
        <button type="button" className="peek-x" onClick={onClose} aria-label="Close">&times;</button>
        <div className="peek-scroll">{children}</div>
      </div>
    </div>
  );
}
